import React, {useEffect, useState} from 'react';
import {Button, Col, Dropdown, Form, InputGroup, Row} from "react-bootstrap";
import Flag from "react-world-flags";
import {CountryCode, CountryCodesPhone, CountryCodesCountry} from "../../config/country-codes";
import UserForm from "./user-form.interface";
import {ValidationError} from "class-validator";

interface SendFormProps {
    submit: (event: React.FormEvent | React.MouseEvent) => void;
    form: UserForm;
    setForm: (form: UserForm) => void;
    errors: ValidationError[];
    setErrors: (errors: ValidationError[]) => void;
}

interface CountryCodeProps {
    country: CountryCode;
}

/**
 * View for selecting country-specific
 * @param {CountryCodeProps} props - The props that the CountryCodeView component accepts.
 * @param {CountryCode} props.countryCode - CountryCode object.
 * @constructor
 */
const CountryCodeView = (props: CountryCodeProps) => {
    return <>
        <Flag code={props.country.code} height={16} width={16} /> {props.country.value}
    </>
}

/**
 * Component with the form at the end of the stepper.
 * @param {SendFormProps} props - The props that the SendForm component accepts.
 * @param {Function} props.submit - Form submit function.
 * @constructor
 */
const SendForm = (props: SendFormProps) => {
    const {setForm, form, errors, submit, setErrors} = props;
    const [firstName, setFirstName] = useState<string>(form.firstName);
    const [lastName, setLastName] = useState<string>(form.lastName);
    const [email, setEmail] = useState<string>(form.email);
    const [phoneCode, setPhoneCode] = useState<CountryCode>(CountryCodesPhone[0]);
    const [phone, setPhone] = useState<string>(form.phone);
    const [streetAndNumber, setStreetAndNumber] = useState<string>(form.streetAndNumber);
    const [postalCode, setPostalCode] = useState<string>(form.postalCode);
    const [city, setCity] = useState<string>(form.city);
    const [country, setCountry] = useState<CountryCode>(CountryCodesCountry[0]);

    const hasError = (field: string) => {
        return errors.filter(e => e.property === field).length > 0;
    }

    const onChange = (field: string, e: React.ChangeEvent<HTMLInputElement>) => {
        setErrors(errors.filter(e => e.property !== field));
        const value = e.target.value;
        switch(field) {
            case 'firstName':
                setFirstName(value);
                break;
            case 'lastName':
                setLastName(value);
                break;
            case 'email':
                setEmail(value);
                break;
            case 'phone':
                setPhone(value);
                break;
            case 'streetAndNumber':
                setStreetAndNumber(value);
                break;
            case 'postalCode':
                setPostalCode(value);
                break;
            case 'city':
                setCity(value);
                break;
        }
    }

    useEffect(() => {
        setForm({
            city,
            country: country.code,
            email,
            firstName,
            lastName,
            phone: phoneCode.value + phone,
            postalCode,
            streetAndNumber
        });
    }, [firstName, lastName, email, phoneCode, phone, streetAndNumber, postalCode, city, country, setForm]);

    return <Form onSubmit={submit}>
        <Form.Group>
            <Form.Label>Voornaam</Form.Label>
            <Form.Control isInvalid={hasError('firstName')} type="text" placeholder="Voornaam" value={firstName} onChange={(e) => onChange('firstName', e as React.ChangeEvent<HTMLInputElement>)} />
        </Form.Group>
        <Form.Group>
            <Form.Label>Familienaam</Form.Label>
            <Form.Control isInvalid={hasError('lastName')} type="text" placeholder="Familienaam" value={lastName} onChange={(e) => onChange('lastName', e as React.ChangeEvent<HTMLInputElement>)} />
        </Form.Group>
        <Form.Group>
            <Form.Label>E-mail</Form.Label>
            <Form.Control isInvalid={hasError('email')} type="email" placeholder="E-mail" value={email} onChange={(e) => onChange('email', e as React.ChangeEvent<HTMLInputElement>)} />
        </Form.Group>
        <Form.Group>
            <Form.Label>Telefoon</Form.Label>
            <InputGroup>
                <InputGroup.Prepend>
                    <Dropdown>
                        <Dropdown.Toggle variant={"white"}>
                            <CountryCodeView country={phoneCode} />
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            {CountryCodesPhone.map(
                                c => <Dropdown.Item key={c.code} onClick={() => setPhoneCode(c)}><CountryCodeView country={c} /></Dropdown.Item>
                            )}
                        </Dropdown.Menu>
                    </Dropdown>
                </InputGroup.Prepend>
                <Form.Control isInvalid={hasError('phone')} type="text" placeholder="Telefoon" value={phone} onChange={(e) => onChange('phone', e as React.ChangeEvent<HTMLInputElement>)} />
            </InputGroup>
        </Form.Group>
        <Form.Group>
            <Form.Label>Straat en nummer</Form.Label>
            <Form.Control isInvalid={hasError('streetAndNumber')} type="text" placeholder="Straat en nummer" value={streetAndNumber} onChange={(e) => onChange('streetAndNumber', e as React.ChangeEvent<HTMLInputElement>)} />
        </Form.Group>
        <Row>
            <Col>
                <Form.Group>
                    <Form.Label>Postcode</Form.Label>
                    <Form.Control isInvalid={hasError('postalCode')} type="text" placeholder="Postcode" value={postalCode} onChange={(e) => onChange('postalCode', e as React.ChangeEvent<HTMLInputElement>)} />
                </Form.Group>
            </Col>
            <Col>
                <Form.Group>
                    <Form.Label>Gemeente</Form.Label>
                    <Form.Control isInvalid={hasError('city')} type="text" placeholder="Gemeente" value={city} onChange={(e) => onChange('city', e as React.ChangeEvent<HTMLInputElement>)} />
                </Form.Group>
            </Col>
        </Row>
        <Form.Group>
            <Form.Label>Land</Form.Label>
            <Dropdown style={{width: '100%'}}>
                <Dropdown.Toggle variant={"white"} style={{width: '100%', textAlign: 'left'}}>
                    <CountryCodeView country={country} />
                </Dropdown.Toggle>
                <Dropdown.Menu>
                    {CountryCodesCountry.map(
                        c => <Dropdown.Item key={c.code} onClick={() => setCountry(c)}><CountryCodeView country={c} /></Dropdown.Item>
                    )}
                </Dropdown.Menu>
            </Dropdown>
        </Form.Group>
        <Button type={'submit'} className={'d-none'} />
    </Form>;
}

export default SendForm;